// 2.D.7 + 2.F.5 — Projektien listanaekymma.
//
// Lista omat + jaetut + (manageri) kaikki. Linkki /projects/:id-detailiin.
// "Uusi projekti" -painike avaa NewProjectDialog:in. 2.F.5 lisasi
// "Aktiiviset" / "Arkistoidut" -tabit.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { apiClient, ApiError, NotAuthenticatedError } from "../api/client.js";
import { NewProjectDialog } from "../components/new-project-dialog.js";
import {
  ProjectDiscoveryBadge,
  type DiscoveredVia,
} from "../components/project-discovery-badge.js";
import type { Project } from "../api/types.js";

type Tab = "active" | "archived";

export function ProjectsList() {
  const [, navigate] = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [tab, setTab] = useState<Tab>("active");
  const [discoveryFilter, setDiscoveryFilter] = useState<"all" | "manual" | "auto">("all");

  function reload() {
    setLoading(true);
    setError(null);
    apiClient
      .listProjects()
      .then((res) => setProjects(res.projects))
      .catch((err) => {
        if (err instanceof NotAuthenticatedError) {
          navigate("/login");
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => setLoading(false));
  }

  useEffect(reload, [navigate]);

  // 2.F.5 — suodata tabin mukaan + 3.A.5 discovery-suodatin
  const visibleProjects = useMemo(() => {
    return projects.filter((p) => {
      const isArchived = p.lifecycleState === "archived";
      if (tab === "archived" ? !isArchived : isArchived) return false;
      // 3.A.5 discovery-filteri
      const via = (p.discoveredVia ?? "manual") as DiscoveredVia;
      if (discoveryFilter === "manual" && via !== "manual") return false;
      if (discoveryFilter === "auto" && via === "manual") return false;
      return true;
    });
  }, [projects, tab, discoveryFilter]);

  const counts = useMemo(() => {
    let active = 0;
    let archived = 0;
    for (const p of projects) {
      if (p.lifecycleState === "archived") archived++;
      else active++;
    }
    return { active, archived };
  }, [projects]);

  return (
    <section className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Projektit</h2>
          <p className="mt-1 text-sm text-slate-500">
            Omat + jaetut. Manager nakee kaikki.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewDialog(true)}
          className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white"
        >
          + Uusi projekti
        </button>
      </header>

      <div className="mb-4 flex items-center gap-3">
        <div className="inline-flex items-center gap-1 rounded-md border border-slate-800 bg-slate-900 p-1">
          <TabBtn label={`Aktiiviset (${counts.active})`} active={tab === "active"} onClick={() => setTab("active")} />
          <TabBtn label={`Arkistoidut (${counts.archived})`} active={tab === "archived"} onClick={() => setTab("archived")} />
        </div>
        <select
          value={discoveryFilter}
          onChange={(e) => setDiscoveryFilter(e.target.value as "all" | "manual" | "auto")}
          className="rounded bg-slate-800 px-3 py-1.5 text-sm text-slate-100"
          title="Suodata projekti-tunnistuksen mukaan"
        >
          <option value="all">Kaikki tunnistustavat</option>
          <option value="manual">Vain manuaaliset</option>
          <option value="auto">Vain auto-discovered</option>
        </select>
      </div>

      {isLoading && <p className="text-slate-500">Ladataan…</p>}
      {error && (
        <div className="rounded-md border border-red-700 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {!isLoading && !error && visibleProjects.length === 0 && (
        <p className="text-slate-500">
          {tab === "archived"
            ? "Ei arkistoituja projekteja."
            : "Ei aktiivisia projekteja. Luo ensimmainen ylakulman painikkeesta."}
        </p>
      )}

      {!isLoading && !error && visibleProjects.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
          {visibleProjects.map((p) => (
            <li key={p.id}>
              <Link
                href={`/projektit/${p.id}`}
                className="block rounded-md border border-slate-800 bg-slate-900/40 p-4 hover:bg-slate-900"
              >
                <h3 className="truncate text-base font-medium text-slate-100">{p.name}</h3>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                  {p.status && (
                    <span className="rounded-full border border-slate-700 px-2 py-0.5">
                      {p.status}
                    </span>
                  )}
                  {p.lifecycleState === "archived" && (
                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2 py-0.5 text-slate-300">
                      Arkistoitu
                    </span>
                  )}
                  <ProjectDiscoveryBadge discoveredVia={p.discoveredVia} />
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <NewProjectDialog
        open={showNewDialog}
        onClose={() => setShowNewDialog(false)}
        onCreated={(p) => {
          setShowNewDialog(false);
          navigate(`/projektit/${p.id}`);
        }}
      />
    </section>
  );
}

function TabBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const cls = active
    ? "rounded-md bg-slate-100 px-3 py-1.5 text-sm text-slate-900"
    : "rounded-md px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800";
  return (
    <button type="button" onClick={onClick} className={cls} aria-pressed={active}>
      {label}
    </button>
  );
}
